// Custom sign-in page — a dark, single-button Google login that matches the
// designer's "Login_Page_design" mock. It replaces Backstage's stock
// core-components <SignInPage> card so the login screen shares the same visual
// language (Geist, indigo accent, #0a0c10 surface) as the rest of the portal.
//
// The identity flow is standard Backstage: on mount we try a silent
// getBackstageIdentity (reuses an existing session, no popup); the button opens
// the Google popup on demand. On success we hand an IdentityApi back to the app
// via props.onSignInSuccess, which is what actually unlocks the shell.
import { SignInPageProps, useApi } from '@backstage/core-plugin-api';
import { googleAuthApiRef } from '@backstage/core-plugin-api';
import { UserIdentity } from '@backstage/core-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { color, font, GlobalStyles, Icon } from '../design';
import logo from './ninetwothree-logo.jpeg';

type ErrorKind = 'denied' | 'network' | 'generic';

const ERRORS: Record<ErrorKind, { title: string; detail: string }> = {
  denied: {
    title: 'Sign-in failed',
    detail:
      'Access is restricted to @ninetwothree.co Google accounts. Contact platform-eng if you believe this is a mistake.',
  },
  network: {
    title: 'Couldn’t reach the identity provider',
    detail: 'Check your connection and try again.',
  },
  generic: {
    title: 'Sign-in failed',
    detail: 'Something went wrong while signing you in. Please try again.',
  },
};

const classifyError = (error: unknown): ErrorKind => {
  const message = (
    error instanceof Error ? error.message : String(error ?? '')
  ).toLowerCase();
  if (/denied|forbidden|not.*allow|restricted|domain|resolve/.test(message)) {
    return 'denied';
  }
  if (/network|failed to fetch|timeout|unreachable|offline/.test(message)) {
    return 'network';
  }
  return 'generic';
};

export const SignInPage = (props: SignInPageProps) => {
  const authApi = useApi(googleAuthApiRef);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorKind | undefined>();
  const [shakeKey, setShakeKey] = useState(0);
  // Guards against calling onSignInSuccess twice (silent + manual race).
  const settled = useRef(false);

  const succeed = useCallback(
    (identity: Parameters<typeof UserIdentity.create>[0]['identity']) => {
      if (settled.current) return;
      settled.current = true;
      props.onSignInSuccess(UserIdentity.create({ identity, authApi }));
    },
    [authApi, props],
  );

  // Silent resume: if a Google session already exists, skip the button.
  useEffect(() => {
    let cancelled = false;
    void authApi
      .getBackstageIdentity({ optional: true })
      .then(result => {
        if (!cancelled && result) succeed(result.identity);
      })
      .catch(() => {
        /* no existing session — show the button */
      });
    return () => {
      cancelled = true;
    };
  }, [authApi, succeed]);

  const signIn = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(undefined);
    try {
      const result = await authApi.getBackstageIdentity({ instantPopup: true });
      if (result) {
        succeed(result.identity);
        return;
      }
      // Popup dismissed without a result — treat as a soft failure.
      setError('denied');
      setShakeKey(k => k + 1);
    } catch (e) {
      setError(classifyError(e));
      setShakeKey(k => k + 1);
    } finally {
      setLoading(false);
    }
  }, [authApi, loading, succeed]);

  const err = error ? ERRORS[error] : undefined;

  return (
    <div
      className="ntt-scope"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: color.bg,
        padding: 20,
        fontFamily: font.sans,
      }}
    >
      <GlobalStyles />

      <div style={{ width: '100%', maxWidth: 376 }}>
        {/* Brand lockup */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: 28,
          }}
        >
          <img
            src={logo}
            alt="NineTwoThree"
            width={40}
            height={40}
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              marginBottom: 14,
              objectFit: 'cover',
              boxShadow: '0 0 0 1px rgba(255,255,255,.08)',
            }}
          />
          <div style={{ fontSize: 15, fontWeight: 600, color: color.textBright }}>
            NineTwoThree
          </div>
          <div
            style={{
              fontSize: 11.5,
              color: color.textDimmest,
              fontFamily: font.mono,
              marginTop: 2,
            }}
          >
            internal development platform
          </div>
        </div>

        {/* Sign-in card */}
        <div
          style={{
            background: color.surface,
            border: `1px solid ${color.border}`,
            borderRadius: 14,
            padding: '28px 26px',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: 22 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: color.textBright,
                marginBottom: 5,
              }}
            >
              Sign in
            </div>
            <div style={{ fontSize: 12.5, color: color.textDim }}>
              Use your NineTwoThree Google account
            </div>
          </div>

          <button
            type="button"
            onClick={signIn}
            disabled={loading}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              height: 42,
              padding: '0 12px',
              background: '#eaecef',
              border: 'none',
              borderRadius: 9,
              color: '#14161a',
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              cursor: loading ? 'default' : 'pointer',
              opacity: loading ? 0.75 : 1,
            }}
          >
            {loading ? (
              <>
                <Icon name="spinner" size={16} spin />
                <span>Signing in…</span>
              </>
            ) : (
              <>
                <GoogleMark />
                <span style={{ whiteSpace: 'nowrap' }}>Sign in with Google</span>
              </>
            )}
          </button>

          {err && (
            <div
              key={shakeKey}
              className="ntt-shake"
              style={{
                display: 'flex',
                gap: 9,
                alignItems: 'flex-start',
                marginTop: 14,
                padding: '11px 12px',
                background: 'rgba(224,108,117,.1)',
                border: '1px solid rgba(224,108,117,.3)',
                borderRadius: 9,
              }}
            >
              <Icon
                name="warning"
                size={15}
                weight="bold"
                color={color.red}
                style={{ flex: 'none', marginTop: 1 }}
              />
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: '#e88b92' }}>
                  {err.title}
                </div>
                <div
                  style={{
                    fontSize: 11.5,
                    color: '#b98c90',
                    marginTop: 2,
                    lineHeight: 1.5,
                  }}
                >
                  {err.detail}
                </div>
              </div>
            </div>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 20,
            }}
          >
            <Icon name="shield-check" size={13} color="#4c545f" />
            <span style={{ fontSize: 11, color: color.textDimmest }}>
              Restricted to @ninetwothree.co accounts
            </span>
          </div>
        </div>
      </div>

      <style>{
        `@keyframes ntt-shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-6px)}40%{transform:translateX(5px)}60%{transform:translateX(-3px)}80%{transform:translateX(2px)}}
         .ntt-shake{animation:ntt-shake .32s ease}`
      }</style>
    </div>
  );
};

// Inline multicolour Google "G" (matches the mock; no icon-font dependency).
const GoogleMark = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" style={{ flex: 'none' }} aria-hidden>
    <path
      fill="#FFC107"
      d="M43.6 20.5H42V20H24v8h11.3C33.9 32.6 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z"
    />
    <path
      fill="#FF3D00"
      d="M6.3 14.7l6.6 4.8C14.6 15.6 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6 29.5 4 24 4c-7.7 0-14.4 4.4-17.7 10.7z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.3 0 10.2-2 13.8-5.4l-6.4-5.4C29.4 34.9 26.8 36 24 36c-5.4 0-9.9-3.4-11.5-8.2l-6.5 5C9.5 39.6 16.2 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.6 20.5H42V20H24v8h11.3c-1 2.8-2.9 5.1-5.4 6.6l6.4 5.4C39.9 37.5 44 31.4 44 24c0-1.3-.1-2.7-.4-3.5z"
    />
  </svg>
);
